<template>
  <card-container
    :id="pageId"
    :is-loading="paymentTiersRes.isLoading"
    loading-message="Loading payment tiers..."
    button-location="top"
  >
    <template #title>
      Payment Tiers
    </template>

    <template #top-form>
      <div
        v-if="canCreatePaymentTiers"
        class="row top-actions"
      >
        <p-button
          type="purple"
          class="ml-20 mobile-ml-10"
          round
          @click="openCreateDialog"
        >
          New Payment Tier
        </p-button>
      </div>

      <div class="row align-items-center">
        <label class="col-sm-3 control-label">Search</label>
        <div class="col-sm-6">
          <sanitized-input
            v-model="searchText"
            placeholder="Search by tier code"
          />
        </div>
      </div>

      <div class="row align-items-center">
        <label class="col-sm-3 control-label">Service</label>
        <div class="col-sm-6">
          <dropdown-list
            v-model="selectedService"
            :options="serviceFilterOptions"
          />
        </div>
      </div>

      <div class="row align-items-center">
        <label class="col-sm-3 control-label">Currency</label>
        <div class="col-sm-6">
          <dropdown-list
            v-model="selectedCurrency"
            :options="currencyFilterOptions"
          />
        </div>
      </div>

      <div class="row align-items-center">
        <label class="col-sm-3 control-label">Active Status</label>
        <div class="col-sm-6">
          <dropdown-list
            v-model="selectedActiveStatus"
            :options="activeStatusFilterOptions"
          />
        </div>
      </div>
    </template>

    <template #content>
      <table-view-container
        :page-id="pageId"
        :data="paymentTiers"
        :table-columns="tableColumns"
      />
    </template>

    <PaymentTierFormDialog
      :modelValue="isFormDialogVisible"
      @update:modelValue="isFormDialogVisible = $event"
      @created="onPaymentTierCreated"
    />
  </card-container>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, onMounted, onUnmounted, ref, watch } from 'vue';
import { debounce, startCase } from 'lodash';

import util from '@/Utils/util';
import { CurrencyType, ServiceType } from '@/gql/graphql';
import { currencyTypeOptions, serviceTypeOptions } from '@/constants/payment';
import { PaymentTierListItem } from '@/models/paymentTier';
import { TableColumnType } from '@/models/table';
import { paymentService } from '@/services/payment';
import { loadPaymentTiers } from '@/use/paymentTiers';
import { useProfile } from '@/use/profile';
import { useSwalDialog } from '@/use/swalDialog';
import { useAsync } from '@/use/useAsync';

const PaymentTierFormDialog = defineAsyncComponent(() => import('./PaymentTiers/PaymentTierFormDialog.vue') as any);

type PaymentTierTableRow = PaymentTierListItem & {
  index: number;
};

const pageId = 'operator-payment-tier-management';

const formatAmount = (value?: number | null) => (value === null || value === undefined ? '-' : util.formatAmountWithCurrency(value));

const tableColumns: TableColumnType[] = [
  { field: 'index', label: 'No', width: 70, isDisableSort: true, isCenterAlign: true },
  { field: 'code', label: 'Code', minWidth: 110 },
  {
    field: 'service',
    label: 'Service',
    minWidth: 140,
    formatter: (value: string) => startCase(value),
  },
  {
    field: 'currency',
    label: 'Currency',
    width: 110,
    isCenterAlign: true,
    formatter: (value: string) => String(value || '').toUpperCase(),
  },
  {
    field: 'unit',
    label: 'Unit',
    width: 110,
    isCenterAlign: true,
    formatter: (value: string) => startCase(value),
  },
  {
    field: 'chargePerOrder',
    label: 'Per Order',
    width: 120,
    isCenterAlign: true,
    formatter: formatAmount,
  },
  {
    field: 'chargePerUnit',
    label: 'Per Unit',
    width: 120,
    isCenterAlign: true,
    formatter: formatAmount,
  },
  {
    field: 'chargePerUnitForSelfOrdering',
    label: 'Self-Order / Unit',
    minWidth: 150,
    isCenterAlign: true,
    formatter: formatAmount,
  },
  {
    field: 'maxCharge',
    label: 'Max Charge',
    width: 120,
    isCenterAlign: true,
    formatter: formatAmount,
  },
  {
    field: 'isActive',
    label: 'Active',
    width: 90,
    isCenterAlign: true,
    formatter: (value: boolean) => (value ? 'Yes' : 'No'),
  },
  {
    field: 'createdAt',
    label: 'Created At',
    width: 180,
    isDateField: true,
  },
];

const searchText = ref('');
const selectedService = ref<ServiceType | ''>('');
const selectedCurrency = ref<CurrencyType | ''>('');
const selectedActiveStatus = ref<'true' | 'false' | ''>('true');
const isFormDialogVisible = ref(false);

const serviceFilterOptions = [{ id: 0, key: '', label: 'All services' }, ...serviceTypeOptions];
const currencyFilterOptions = [{ id: 0, key: '', label: 'All currencies' }, ...currencyTypeOptions];
const activeStatusFilterOptions = [
  { id: 0, key: '', label: 'All' },
  { id: 1, key: 'true', label: 'Active' },
  { id: 2, key: 'false', label: 'Inactive' },
];

const { showErrorPopupDialog } = useSwalDialog();
const { userRoles } = useProfile();

const canCreatePaymentTiers = computed(() => userRoles.isAdmin || userRoles.isDirector);

const normalizePaymentTier = (tier: PaymentTierListItem): PaymentTierListItem => ({
  ...tier,
  currency: String(tier.currency).toLowerCase() as CurrencyType,
  service: String(tier.service).toLowerCase() as ServiceType,
});

const getTierNumber = (code: string) => Number(code.slice(4));

const sortPaymentTiers = (tiers: PaymentTierListItem[]) =>
  [...tiers].sort((left, right) => {
    const serviceCompare = left.service.localeCompare(right.service);
    if (serviceCompare !== 0) {
      return serviceCompare;
    }

    const currencyCompare = left.currency.localeCompare(right.currency);
    if (currencyCompare !== 0) {
      return currencyCompare;
    }

    return getTierNumber(left.code) - getTierNumber(right.code);
  });

const fetchPaymentTiersForFilters = async () => {
  const service = selectedService.value || undefined;
  const currency = selectedCurrency.value || undefined;

  if (selectedActiveStatus.value === '') {
    const [activeTiers, inactiveTiers] = await Promise.all([
      paymentService.fetchPaymentTiers({ service, currency, isActive: true }),
      paymentService.fetchPaymentTiers({ service, currency, isActive: false }),
    ]);

    return sortPaymentTiers([...activeTiers, ...inactiveTiers].map(normalizePaymentTier));
  }

  const tiers = await paymentService.fetchPaymentTiers({
    service,
    currency,
    isActive: selectedActiveStatus.value === 'true',
  });

  return sortPaymentTiers(tiers.map(normalizePaymentTier));
};

const [paymentTiersRes, loadPaymentTierRows] = useAsync(async (): Promise<PaymentTierTableRow[]> => {
  const search = searchText.value.trim().toLowerCase();
  const rows = await fetchPaymentTiersForFilters();
  const filteredRows = search ? rows.filter((tier) => tier.code.toLowerCase().includes(search)) : rows;

  return filteredRows.map((tier, index) => ({
    ...tier,
    index: index + 1,
  }));
});

const paymentTiers = computed(() => paymentTiersRes.value ?? []);

watch(paymentTiersRes, () => {
  if (paymentTiersRes.isLoading) {
    return;
  }

  if (paymentTiersRes.error) {
    showErrorPopupDialog({
      text: util.getGraphqlError(paymentTiersRes.error, false),
    });
  }
});

const debouncedLoadPaymentTierRows = debounce(() => loadPaymentTierRows().catch(() => undefined), 300);

watch(searchText, () => {
  debouncedLoadPaymentTierRows();
});

watch([selectedService, selectedCurrency, selectedActiveStatus], () => {
  loadPaymentTierRows().catch(() => undefined);
});

onUnmounted(() => {
  debouncedLoadPaymentTierRows.cancel();
});

const openCreateDialog = () => {
  isFormDialogVisible.value = true;
};

const onPaymentTierCreated = async () => {
  loadPaymentTiers();
  await loadPaymentTierRows().catch(() => undefined);
};

onMounted(() => {
  loadPaymentTierRows().catch(() => undefined);
});
</script>

<style scoped lang="scss">
.top-actions {
  margin-bottom: 10px;
}
</style>
